import { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import PrizeDataBuilder from "@/components/PrizeDataBuilder";
import { parsePrizeJson } from "@/components/PrizePool";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Trophy, ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Users, ExternalLink, Clapperboard, Medal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useAdminChallengesQuery,
  useAdminCreateChallengeMutation,
  useAdminUpdateChallengeMutation,
  useAdminDeleteChallengeMutation,
  AdminChallengesQuery,
} from "@/generated/graphql";

// The list view selects a narrower subset of fields than the full AdminChallenge
// type, so handlers must accept exactly what the query returns.
type AdminChallenge = NonNullable<NonNullable<AdminChallengesQuery['adminChallenges']>['data']>[number];
import { formatDateTime } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";
import { wallClockToUTC } from "@/lib/utils";

interface ChallengeFormData {
  name: string;
  description: string;
  prize_pool: number;
  warning_message: string;
  rules_allowed: string;
  rules_forbidden: string;
  rules_attention: string;
  is_accepting_participants: boolean;
  required_hashtags: string;
  caption_examples: string;
  prize_data: string;
}

const initialFormData: ChallengeFormData = {
  name: "",
  description: "",
  prize_pool: 0,
  warning_message: "",
  rules_allowed: "",
  rules_forbidden: "",
  rules_attention: "",
  is_accepting_participants: true,
  required_hashtags: "",
  caption_examples: "",
  prize_data: "",
};

const AdminChallenges = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState<AdminChallenge | null>(null);
  const [formData, setFormData] = useState<ChallengeFormData>(initialFormData);
  const [prizeDataValid, setPrizeDataValid] = useState(true);

  const { data, loading, error, refetch } = useAdminChallengesQuery({
    variables: { page, pageSize, search: search || undefined },
    fetchPolicy: "cache-and-network",
  });

  const [createChallenge, { loading: creating }] = useAdminCreateChallengeMutation();
  const [updateChallenge, { loading: updating }] = useAdminUpdateChallengeMutation();
  const [deleteChallenge, { loading: deleting }] = useAdminDeleteChallengeMutation();

  const challenges = data?.adminChallenges?.data || [];
  const totalPages = data?.adminChallenges?.pageCount || 1;
  const total = data?.adminChallenges?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleInputChange = (field: keyof ChallengeFormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedChallenge(null);
    setPrizeDataValid(true);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const openEditModal = (challenge: AdminChallenge) => {
    setSelectedChallenge(challenge);
    setFormData({
      name: challenge.name,
      description: challenge.description || "",
      prize_pool: challenge.prize_pool,
      warning_message: challenge.warning_message || "",
      rules_allowed: challenge.rules_allowed || "",
      rules_forbidden: challenge.rules_forbidden || "",
      rules_attention: challenge.rules_attention || "",
      is_accepting_participants: challenge.is_accepting_participants ?? true,
      required_hashtags: challenge.required_hashtags || "",
      caption_examples: challenge.caption_examples || "",
      prize_data: challenge.prize_data || "",
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (challenge: AdminChallenge) => {
    setSelectedChallenge(challenge);
    setIsDeleteModalOpen(true);
  };

  const buildInput = () => {
    return {
      name: formData.name,
      description: formData.description || undefined,
      prize_pool: formData.prize_pool,
      warning_message: formData.warning_message || undefined,
      rules_allowed: formData.rules_allowed || undefined,
      rules_forbidden: formData.rules_forbidden || undefined,
      rules_attention: formData.rules_attention || undefined,
      is_accepting_participants: formData.is_accepting_participants,
      required_hashtags: formData.required_hashtags || undefined,
      caption_examples: formData.caption_examples || undefined,
      prize_data: (() => {
        if (!formData.prize_data) return undefined;
        const parsed = parsePrizeJson(formData.prize_data);
        return parsed ? JSON.stringify(parsed) : undefined;
      })(),
    };
  };

  const handleCreate = async () => {
    try {
      await createChallenge({ variables: { input: buildInput() } });
      toast({ title: t("admin.challenges.toast.created") });
      setIsCreateModalOpen(false);
      resetForm();
      refetch();
    } catch (err) {
      console.error("Failed to create challenge:", err);
      toast({ title: t("admin.challenges.toast.createError"), variant: "destructive" });
    }
  };

  const handleUpdate = async () => {
    if (!selectedChallenge) return;
    try {
      await updateChallenge({ variables: { id: selectedChallenge.id, input: buildInput() } });
      toast({ title: t("admin.challenges.toast.updated") });
      setIsEditModalOpen(false);
      resetForm();
      refetch();
    } catch (err) {
      console.error("Failed to update challenge:", err);
      toast({ title: t("admin.challenges.toast.updateError"), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!selectedChallenge) return;
    try {
      await deleteChallenge({ variables: { id: selectedChallenge.id } });
      toast({ title: t("admin.challenges.toast.deleted") });
      setIsDeleteModalOpen(false);
      setSelectedChallenge(null);
      refetch();
    } catch (err) {
      console.error("Failed to delete challenge:", err);
      toast({ title: t("admin.challenges.toast.deleteError"), variant: "destructive" });
    }
  };

  const getChallengeStatus = (challenge: AdminChallenge) => {
    if (!challenge.start_date || !challenge.end_date) return "upcoming";
    const tz = (challenge.rankings && challenge.rankings[0]?.timezone) || 'America/Sao_Paulo';
    const now = new Date();
    const start = wallClockToUTC(challenge.start_date, tz);
    const end = wallClockToUTC(challenge.end_date, tz);
    if (now < start) return "upcoming";
    if (now > end) return "ended";
    return "active";
  };

  const statusBadge = (challenge: AdminChallenge) => {
    const status = getChallengeStatus(challenge);
    switch (status) {
      case "active":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">{t("admin.challenges.active")}</Badge>;
      case "ended":
        return <Badge variant="outline" className="text-red-500 border-red-500">{t("admin.challenges.ended")}</Badge>;
      case "upcoming":
        return <Badge variant="outline" className="text-amber-500 border-amber-500">{t("admin.challenges.upcoming")}</Badge>;
    }
  };

  const challengeFormFields = (prefix: string) => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-name`}>{t("admin.challenges.name")}</Label>
          <Input id={`${prefix}-name`} value={formData.name} onChange={(e) => handleInputChange("name", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-prize_pool`}>{t("admin.challenges.prizePool")}</Label>
          <Input id={`${prefix}-prize_pool`} type="number" min="0" value={formData.prize_pool} onChange={(e) => handleInputChange("prize_pool", parseFloat(e.target.value) || 0)} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${prefix}-description`}>{t("admin.challenges.description")}</Label>
        <Textarea id={`${prefix}-description`} value={formData.description} onChange={(e) => handleInputChange("description", e.target.value)} rows={2} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${prefix}-warning_message`}>{t("admin.challenges.warningMessage")}</Label>
        <Textarea id={`${prefix}-warning_message`} value={formData.warning_message} onChange={(e) => handleInputChange("warning_message", e.target.value)} rows={2} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${prefix}-rules_allowed`}>{t("admin.challenges.rulesAllowed")}</Label>
        <Textarea id={`${prefix}-rules_allowed`} value={formData.rules_allowed} onChange={(e) => handleInputChange("rules_allowed", e.target.value)} rows={3} placeholder={t("admin.challenges.onePerLine")} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${prefix}-rules_forbidden`}>{t("admin.challenges.rulesForbidden")}</Label>
        <Textarea id={`${prefix}-rules_forbidden`} value={formData.rules_forbidden} onChange={(e) => handleInputChange("rules_forbidden", e.target.value)} rows={3} placeholder={t("admin.challenges.onePerLine")} />
      </div>

      <div className="flex items-center justify-between rounded-lg border border-border p-3">
        <div>
          <Label htmlFor={`${prefix}-is_accepting_participants`} className="text-sm font-medium cursor-pointer">
            {t("admin.challenges.isAcceptingParticipants")}
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">{t("admin.challenges.isAcceptingParticipantsHint")}</p>
        </div>
        <Switch
          id={`${prefix}-is_accepting_participants`}
          checked={formData.is_accepting_participants}
          onCheckedChange={(checked) => handleInputChange("is_accepting_participants", checked)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${prefix}-rules_attention`}>{t("admin.challenges.rulesAttention")}</Label>
        <Textarea id={`${prefix}-rules_attention`} value={formData.rules_attention} onChange={(e) => handleInputChange("rules_attention", e.target.value)} rows={3} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${prefix}-required_hashtags`}>{t("admin.challenges.requiredHashtags")}</Label>
        <Textarea id={`${prefix}-required_hashtags`} value={formData.required_hashtags} onChange={(e) => handleInputChange("required_hashtags", e.target.value)} rows={2} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${prefix}-caption_examples`}>{t("admin.challenges.captionExamples")}</Label>
        <Textarea id={`${prefix}-caption_examples`} value={formData.caption_examples} onChange={(e) => handleInputChange("caption_examples", e.target.value)} rows={5} placeholder={t("admin.challenges.onePerLine")} />
      </div>

      <div className="space-y-2">
        <Label>{t("admin.challenges.prizeData")}</Label>
        <PrizeDataBuilder
          key={prefix === "create" ? "create" : (selectedChallenge?.id || "edit")}
          value={formData.prize_data}
          onChange={(v) => handleInputChange("prize_data", v)}
          onValidationChange={(v) => setPrizeDataValid(v)}
        />
      </div>
    </div>
  );

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t("admin.challenges.title")}</h1>
                <p className="text-muted-foreground">{t("admin.challenges.subtitle")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
              <Button onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" />
                {t("admin.challenges.createChallenge")}
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.challenges.list")}</CardTitle>
                <span className="text-sm text-muted-foreground">{t("admin.common.total")}: {total}</span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2 mb-4">
                <Input
                  placeholder={t("admin.challenges.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-sm"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {loading && !data ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">{t("admin.common.error")}</div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.challenges.name")}</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>{t("admin.challenges.startDate")}</TableHead>
                          <TableHead>{t("admin.challenges.endDate")}</TableHead>
                          <TableHead>{t("admin.challenges.prizePool")}</TableHead>
                          <TableHead>{t("admin.challenges.participantCount")}</TableHead>
                          <TableHead>{t("admin.challenges.createdAt")}</TableHead>
                          <TableHead className="w-[140px]">{t("admin.challenges.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {challenges.map((challenge) => (
                          <TableRow key={challenge.id}>
                            <TableCell className="font-medium">{challenge.name}</TableCell>
                            <TableCell>{statusBadge(challenge)}</TableCell>
                            <TableCell>{challenge.start_date ? new Date(challenge.start_date).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—"}</TableCell>
                            <TableCell>{challenge.end_date ? new Date(challenge.end_date).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—"}</TableCell>
                            <TableCell>R$ {challenge.prize_pool?.toLocaleString("pt-BR")}</TableCell>
                            <TableCell>{challenge.participants?.length || 0}</TableCell>
                            <TableCell>{formatDateTime(challenge.createdAt)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => navigate(`/challenge/${challenge.id}`)} title={t("admin.challenges.viewChallenge")}>
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/challenges/${challenge.id}/participants`)} title={t("admin.challenges.participants")}>
                                  <Users className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/challenges/${challenge.id}/clips`)} title={t("admin.challenges.viewClips")}>
                                  <Clapperboard className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/challenges/${challenge.id}/rankings`)} title={t("admin.challenges.rankings")}>
                                  <Medal className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openEditModal(challenge)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openDeleteModal(challenge)} className="text-red-500 hover:text-red-600">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {challenges.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                              {t("admin.common.noResults")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {page} {t("admin.common.of")} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-3xl lg:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t("admin.challenges.createChallenge")}</DialogTitle>
            <DialogDescription>{t("admin.challenges.createChallengeDesc")}</DialogDescription>
          </DialogHeader>
          {challengeFormFields("create")}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCreate} disabled={creating || !formData.name || !prizeDataValid}>
              {creating ? t("admin.challenges.creating") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-3xl lg:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t("admin.challenges.editChallenge")}</DialogTitle>
            <DialogDescription>{t("admin.challenges.editChallengeDesc")}</DialogDescription>
          </DialogHeader>
          {challengeFormFields("edit")}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleUpdate} disabled={updating || !formData.name || !prizeDataValid}>
              {updating ? t("admin.challenges.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.challenges.deleteChallenge")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.challenges.deleteConfirm", { name: selectedChallenge?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-500 hover:bg-red-600">
              {deleting ? t("admin.challenges.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </Layout>
  );
};

export default AdminChallenges;
